import { useQuery } from '@tanstack/react-query'
import { Link, useParams } from 'react-router-dom'
import { QueryState } from '../components/QueryState'
import { api } from '../lib/api'
import { URGENCY_LABEL, makeLabeler, useCommonCodes, useTargetCodes } from '../lib/codes'

const IMPACT_SCOPE_LABEL: Record<string, string> = {
  personal: '나 한 사람',
  neighbor: '이웃 몇 사람',
  dong_wide: '동네 전체',
}

const DESIRED_PERIOD_LABEL: Record<string, string> = {
  immediate: '지금 당장',
  within_3m: '3개월 안',
  within_6m: '6개월 안',
  anytime: '언제든',
}

/**
 * 시민문제 상세.
 * 매칭 결과 영역은 아직 없다 — 적합도 산식이 확정 전이라 만들지 않는다(확정요청서 B-1, 진행설계 §5-B).
 * 빈 값은 감추지 않고 "확인 필요"로 적는다. 감추면 정보 없음과 미입력을 구분할 수 없다(설계서 §4-4).
 */
export function ProblemDetailPage() {
  const { id } = useParams<{ id: string }>()
  const problem = useQuery({
    queryKey: ['problem-card', id],
    queryFn: () => api.problemCard(id ?? ''),
    enabled: Boolean(id),
  })

  const categoryLabel = makeLabeler(useCommonCodes('category').data)
  const targetLabel = makeLabeler(useTargetCodes().data)
  const resourceLabel = makeLabeler(useCommonCodes('resource').data)

  const p = problem.data
  const unknown = <span className="muted">확인 필요</span>

  return (
    <>
      <Link className="back-link" to="/problems">
        ← 시민문제카드 목록
      </Link>
      <QueryState isPending={problem.isPending} error={problem.error}>
        {p ? (
          <article className="panel">
            <div className="detail-head">
              <div className="card-org">
                {p.region.name}{' '}
                <span className={`urgency u-${p.urgency}`}>
                  {URGENCY_LABEL[p.urgency] ?? p.urgency}
                </span>
              </div>
              <h1 className="page-title">{p.title}</h1>
              {/* 머리에는 요약만 둔다 — 원문은 아래 별도 블록이다.
                  아직 확정 전이면 그리지 않는다(빈 문단을 남기지 않는다). */}
              {p.summary !== null && (
                <p className="muted" style={{ margin: 0 }}>
                  {p.summary}
                </p>
              )}
            </div>

            <dl className="kv">
              <dt>분야</dt>
              <dd>{p.categoryCodes?.length ? p.categoryCodes.map(categoryLabel).join(' · ') : unknown}</dd>

              <dt>대상</dt>
              <dd>{p.targetCodes.map(targetLabel).join(' · ')}</dd>

              <dt>필요한 도움</dt>
              <dd>
                {p.neededResourceCodes?.length
                  ? p.neededResourceCodes.map(resourceLabel).join(' · ')
                  : unknown}
              </dd>

              <dt>영향 범위</dt>
              <dd>{p.impactScope ? (IMPACT_SCOPE_LABEL[p.impactScope] ?? p.impactScope) : unknown}</dd>

              <dt>희망 시기</dt>
              <dd>{p.desiredPeriod ? (DESIRED_PERIOD_LABEL[p.desiredPeriod] ?? p.desiredPeriod) : unknown}</dd>

              <dt>어려운 점</dt>
              <dd>{p.barrierNote ?? unknown}</dd>

              <dt>필요한 자원</dt>
              <dd>{p.neededResourceNote ?? unknown}</dd>
            </dl>

            {/* 시민이 쓴 원문. **없을 수 있는 이유가 둘이다** — 키 자체가 없으면 서버가 원문을
                닫은 것이고(08-31 한시 개방, 이티랩 결정에 따라 닫힌다), `null`이면 원문 행이
                없는 카드(구글폼 이관분)다. 어느 쪽이든 화면은 요약만으로 성립해야 해서
                이 블록만 빠진다 — "확인 필요"를 적지 않는다(미입력 항목이 아니다). */}
            {p.rawText ? (
              <section style={{ marginTop: 24 }}>
                <h2 className="section-title">시민이 쓴 내용</h2>
                <p style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{p.rawText}</p>
              </section>
            ) : null}

            {/* AI 추출 키워드(041). 상세에만 오고, 목록 응답에는 키 자체가 없다.
                빈 배열·키 없음은 «뽑을 것이 없었다»와 «AI를 못 돌렸다»가 합쳐진 값이라
                구분해 적지 않고 블록만 빠진다(원문 블록과 같은 규칙). `#`는 여기서 붙인다 —
                값에는 없다. 코드값이 아니라 눌러도 필터가 걸리지 않는다(표시 전용). */}
            {p.keywords && p.keywords.length > 0 ? (
              <section style={{ marginTop: 24 }}>
                <h2 className="section-title">AI 추출 키워드</h2>
                <div className="tags">
                  {p.keywords.map((k) => (
                    <span className="tag" key={k}>
                      # {k}
                    </span>
                  ))}
                </div>
              </section>
            ) : null}

            <div className="notice" style={{ marginTop: 24 }}>
              이 문제에 맞는 기업카드를 찾아 연결하는 기능은 준비 중입니다.
            </div>
          </article>
        ) : null}
      </QueryState>
    </>
  )
}
